import { useQuery } from '@tanstack/react-query'
import { api } from '../api/client'
import { fetchTsIds } from './shared/parallelFetch'
import { useToFill } from './shared/useToFill'

// Normály pro skupinové produkty "Měsíční / Roční data – skupina" — paralelní sndata_ts fetch přes
// STANICE (obdoba useMonthlyNormals/useYearlyNormals, ale vnější smyčka je přes ghIds).
// Viz doc/plans/station-group-monthly-yearly-phenomena.md sekce 4b.
//
// kind: 'mly' → měsíční normály (klíč = row.sid '01'..'12') + roční normál (klíč 'YEAR')
//       'yly' → roční normál (klíč 'YEAR', stejný pro všechny roky rozsahu)
//
// Vrací normalsMap: Map<gh_id, Map<normalKey, Map<tsId, average>>>
export function useGroupPeriodNormals(kind, ghIds, tsIds, enabled = true) {
  const isMonth = kind === 'mly'

  const queryKey = ['groupPeriodNormals', kind, [...ghIds].sort(), [...tsIds].sort()]

  const { data, isLoading, isError } = useQuery({
    queryKey,
    queryFn: async () => {
      const empty = { data: [], to_fill: [] }
      const perGh = await fetchTsIds(
        ghIds,
        async ghId => {
          const yearN = await fetchTsIds(
            tsIds,
            tsId => api.get(`/sndata_ts?ts_id=${tsId}&gh_id=${ghId}&stype=YEAR`).catch(() => empty),
            true
          )
          if (!isMonth) return { yearN }
          const monthN = await fetchTsIds(
            tsIds,
            tsId => api.get(`/sndata_ts?ts_id=${tsId}&gh_id=${ghId}&stype=MONTH`).catch(() => empty),
            true
          )
          return { monthN, yearN }
        },
        true
      )
      const result = perGh.map(g => ({
        monthN: (g.monthN ?? []).map(r => r?.data ?? []),
        yearN: (g.yearN ?? []).map(r => r?.data ?? []),
      }))
      const toFill = perGh.flatMap(g =>
        [...(g.monthN ?? []), ...(g.yearN ?? [])].flatMap(r => r?.to_fill ?? [])
      )
      return { result, toFill }
    },
    enabled: enabled && ghIds.length > 0 && tsIds.length > 0,
    staleTime: 60 * 60 * 1000,
  })

  useToFill(queryKey, data, (result, resp, item) => {
    const gi = ghIds.indexOf(item.params?.gh_id)
    const ti = tsIds.indexOf(item.params?.ts_id)
    if (gi < 0 || ti < 0) return result
    const bucket = item.params?.stype === 'YEAR' ? 'yearN' : 'monthN'
    const next = result.slice()
    const g = { ...next[gi] }
    const arr = g[bucket].slice()
    arr[ti] = resp.data
    g[bucket] = arr
    next[gi] = g
    return next
  })

  const normalsMap = new Map()
  if (data?.result) {
    ghIds.forEach((ghId, gi) => {
      const perGh = data.result[gi] ?? {}
      const keyMap = new Map()
      const put = (key, tsId, avg) => {
        const entry = keyMap.get(key) ?? new Map()
        entry.set(tsId, avg ?? null)
        keyMap.set(key, entry)
      }
      tsIds.forEach((tsId, ti) => {
        if (isMonth) {
          ;(perGh.monthN?.[ti] ?? []).forEach(row => { if (row.sid) put(row.sid, tsId, row.average) })
        }
        const yRows = perGh.yearN?.[ti] ?? []
        put('YEAR', tsId, yRows.length > 0 ? yRows[0]?.average : null)
      })
      normalsMap.set(ghId, keyMap)
    })
  }

  return { normalsMap, isLoading, isError }
}
